<template>
  <div
    class="slot"
    :class="{
      filled: content !== null,
      empty: content === null,
      editing: isEditMode,
      selected: isSelected,
      dragOver: isDragOver,
      'height-screen': heightMode === 'screen',
    }"
    @dragenter.prevent.stop="onDragEnter"
    @dragover.prevent.stop="onDragOver"
    @dragleave.stop="onDragLeave"
    @drop.prevent.stop="onDrop"
    @click="onClick"
  >
    <template v-if="content">
      <!-- Removal lives in the inspector: select the slot, then Clear. -->
      <div
        v-if="content.widget.kind === 'layout'"
        class="layout-grid"
        :style="layoutGridStyle"
      >
        <CanvasSlotNode
          v-for="(child, index) in content.widget.slots"
          :key="index"
          :style="layoutChildStyle(index)"
          :content="child"
          :path="[...path, index]"
          :height-mode="heightMode"
          :is-edit-mode="isEditMode"
          :selected-path="selectedPath"
          :workspace-id="workspaceId"
          :query-params="queryParams"
          :view-parameters="viewParameters"
          :storage-prefix="storagePrefix"
          :parent-id="parentId"
          :widget-date="widgetDate"
          :calendars-map="calendarsMap"
          :view-config-host="viewConfigHost"
          :drop-widget="dropWidget"
          :remove-widget="removeWidget"
          :select-slot="selectSlot"
          :update-query="updateQuery"
          :update-display="updateDisplay"
          :resize-layout-tracks="resizeLayoutTracks"
        />
        <!-- Track resizers (edit mode): each 8px track between two slots
             hosts a handle whose drag trades axis share between its
             neighbors — proportions, not pixels (see `resizeTrackPair`). -->
        <template v-if="isEditMode">
          <div
            v-for="separator in content.widget.columns - 1"
            :key="`column-separator-${separator}`"
            class="track-separator columns"
            :style="{ gridColumn: `${2 * separator}`, gridRow: '1 / -1' }"
            @pointerdown="startTrackResize($event, 'columns', separator - 1)"
            @click.stop
          />
          <div
            v-for="separator in content.widget.rows - 1"
            :key="`row-separator-${separator}`"
            class="track-separator rows"
            :style="{ gridRow: `${2 * separator}`, gridColumn: '1 / -1' }"
            @pointerdown="startTrackResize($event, 'rows', separator - 1)"
            @click.stop
          />
        </template>
      </div>
      <!-- Display-controlled (`:display` is never left undefined): layout
           and per-layout preferences live in the widget's display document
           saved with the canvas body, not in this device's browser store. -->
      <View
        v-else-if="content.widget.kind === 'view'"
        :workspace-id="workspaceId"
        :query="viewQuery"
        :display="viewDisplay"
        :query-params="queryParams"
        :parent-id="parentId"
        :storage-key-prefix="`${storagePrefix}/${content.id}`"
        :parameters="viewParameters"
        :selected="isSelected"
        :config-host="isSelected ? viewConfigHost : null"
        @update:query="updateQuery(content.id, $event)"
        @update:display="updateDisplay(content.id, $event)"
        @delete="removeWidget(content.id)"
      />
      <Suspense v-else-if="content.widget.kind === 'timeline'">
        <Events :date="widgetDate" :calendars-map="calendarsMap" />
        <template #fallback>
          <Loader><BaseTimelineSkeleton /></Loader>
        </template>
      </Suspense>
      <Suspense v-else-if="content.widget.kind === 'tasks'">
        <CanvasTasksWidget
          :sections="content.widget.sections"
          :date="widgetDate"
          :storage-prefix="`${storagePrefix}/${content.id}`"
        />
        <template #fallback>
          <Loader><BaseSkeletonList /></Loader>
        </template>
      </Suspense>
      <Suspense v-else-if="content.widget.kind === 'calendar'">
        <CanvasCalendarWidget
          :date="widgetDate"
          :layout="content.widget.layout ?? 'week'"
          :timezones="content.widget.timezones !== false"
          :zoom="content.widget.zoom !== false"
          :layouts="content.widget.layouts !== false"
          :footer="content.widget.footer !== false"
          :storage-prefix="`${storagePrefix}/${content.id}`"
        />
        <template #fallback>
          <Loader><BaseTimelineSkeleton /></Loader>
        </template>
      </Suspense>
    </template>
    <div v-else-if="isEditMode" class="slot-placeholder">
      <span>Drop a widget here</span>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, toRefs, type PropType } from "vue";

import type {
  GraphVariableDictionary,
  WorkspaceId,
} from "@ctrl/controller-rpc";

import Events from "@/components/agenda/Events.vue";
import BaseSkeletonList from "@/components/base/BaseSkeletonList.vue";
import BaseTimelineSkeleton from "@/components/base/BaseTimelineSkeleton.vue";
import type { ViewParameters } from "@/components/inject";
import Loader from "@/components/Loader.vue";
import View, { type Query } from "@/components/views/View.vue";
import { isRecord } from "@/utils/unknown";

import {
  resizeTrackPair,
  type CanvasSlotPath,
  type CanvasTabHeight,
  type CanvasWidget,
} from "./body";
import CanvasCalendarWidget from "./CanvasCalendarWidget.vue";
import CanvasTasksWidget from "./CanvasTasksWidget.vue";
import {
  hasWidgetDropPayload,
  readWidgetDropPayload,
  type CanvasWidgetDropPayload,
} from "./dnd";

/** Which grid axis a track separator resizes. */
type TrackAxis = "rows" | "columns";

/** Width of the separator tracks between slots (the former grid gap). */
const GRID_GAP = 8;

/**
 * One slot of a canvas: empty (a drop target in edit mode, blank space
 * otherwise) or holding a widget. Layout widgets render a grid of child
 * slots through this same component, recursively.
 *
 * Mutations are routed to the owning `Canvas.vue` through function props
 * (rather than emits) so they don't need re-emitting at every nesting level.
 */
export default defineComponent({
  name: "CanvasSlotNode",
  components: {
    BaseSkeletonList,
    BaseTimelineSkeleton,
    CanvasCalendarWidget,
    CanvasTasksWidget,
    Events,
    Loader,
    View,
  },
  props: {
    content: {
      type: Object as PropType<CanvasWidget | null>,
      default: null,
    },
    /** Slot path from the tab root (child slot indexes). */
    path: {
      type: Array as PropType<CanvasSlotPath>,
      required: true,
    },
    /**
     * The tab's vertical behavior (see `CanvasTabHeight`): in "screen"
     * mode slots divide a fixed height and widgets scroll internally; in
     * "page" mode slots grow with their content.
     */
    heightMode: {
      type: String as PropType<CanvasTabHeight>,
      default: "screen",
    },
    isEditMode: { type: Boolean, default: false },
    /** Path of the selected slot (empty slots are selectable too). */
    selectedPath: {
      type: Array as PropType<CanvasSlotPath | null>,
      default: null,
    },
    workspaceId: {
      type: String as PropType<WorkspaceId>,
      required: true,
    },
    queryParams: {
      type: Object as PropType<GraphVariableDictionary>,
      required: true,
    },
    viewParameters: {
      type: Object as PropType<ViewParameters>,
      required: true,
    },
    /** Per-widget browser-store prefix; `/${widget.id}` is appended. */
    storagePrefix: { type: String, required: true },
    /** The canvas object id, passed to views as their parent. */
    parentId: { type: String, required: true },
    widgetDate: { type: Date, required: true },
    calendarsMap: {
      type: Object as PropType<Record<string, unknown>>,
      required: true,
    },
    /** The inspector pane element hosting the selected view widget's
     * configuration (see `View.configHost`); only handed to the selected
     * view. */
    viewConfigHost: {
      type: Object as PropType<HTMLElement | null>,
      default: null,
    },
    dropWidget: {
      type: Function as PropType<
        (path: CanvasSlotPath, payload: CanvasWidgetDropPayload) => void
      >,
      required: true,
    },
    removeWidget: {
      type: Function as PropType<(id: string) => void>,
      required: true,
    },
    selectSlot: {
      type: Function as PropType<(path: CanvasSlotPath | null) => void>,
      required: true,
    },
    updateQuery: {
      type: Function as PropType<(id: string, query: Query) => void>,
      required: true,
    },
    /** Store a view widget's emitted display document (opaque JSON). */
    updateDisplay: {
      type: Function as PropType<(id: string, display: unknown) => void>,
      required: true,
    },
    /** Persist a layout widget's track sizes after a separator drag. */
    resizeLayoutTracks: {
      type: Function as PropType<
        (id: string, axis: TrackAxis, sizes: number[]) => void
      >,
      required: true,
    },
  },
  setup(props) {
    const { content, isEditMode, selectedPath } = toRefs(props);

    // Selection is by path, so empty slots highlight too.
    const isSelected = computed(() => {
      const selected = selectedPath.value;
      return (
        isEditMode.value &&
        selected !== null &&
        selected.length === props.path.length &&
        selected.every((value, index) => props.path[index] === value)
      );
    });

    const viewQuery = computed<Query>(() => ({
      current:
        content.value?.widget.kind === "view" ? content.value.widget.query : "",
    }));

    // The widget's saved display document; `null` (never undefined) keeps
    // the View display-controlled even before its first emit, so a fresh
    // widget starts from the views module's defaults instead of the store.
    const viewDisplay = computed<Record<string, unknown> | null>(() => {
      const body = content.value?.widget;
      if (body?.kind !== "view") return null;
      return isRecord(body.display) ? body.display : null;
    });

    // A drag in progress previews its sizes locally; the body is only
    // updated on release (see `startTrackResize`).
    const trackDrag = ref<{ axis: TrackAxis; sizes: number[] } | null>(null);

    // Slot tracks interleaved with fixed separator tracks (the former grid
    // gap): "1fr 8px 1fr". The separators are grid items, so their hit
    // areas follow the tracks exactly.
    const layoutGridStyle = computed(() => {
      const body = content.value?.widget;
      if (body?.kind !== "layout") return undefined;
      const drag = trackDrag.value;
      const track = (
        sizes: number[] | undefined,
        count: number,
        override: number[] | undefined
      ): string =>
        (override ?? sizes ?? Array.from({ length: count }, () => 1))
          .map((size) => `${size}fr`)
          .join(` ${GRID_GAP}px `);
      return {
        gridTemplateRows: track(
          body.rowSizes,
          body.rows,
          drag?.axis === "rows" ? drag.sizes : undefined
        ),
        gridTemplateColumns: track(
          body.columnSizes,
          body.columns,
          drag?.axis === "columns" ? drag.sizes : undefined
        ),
      };
    });

    // Grid lines: slot (row, column) lives at track 2*row+1 / 2*column+1
    // (odd tracks are slots, even tracks are separators).
    const layoutChildStyle = (index: number) => {
      const body = content.value?.widget;
      if (body?.kind !== "layout") return undefined;
      const row = Math.floor(index / body.columns);
      const column = index % body.columns;
      return {
        gridRow: `${2 * row + 1}`,
        gridColumn: `${2 * column + 1}`,
      };
    };

    // Drag a separator: trade axis share between the two adjacent tracks
    // (`resizeTrackPair` normalizes and clamps), preview locally on every
    // move, persist once on release.
    const startTrackResize = (
      event: PointerEvent,
      axis: TrackAxis,
      index: number
    ) => {
      const body = content.value?.widget;
      const widgetId = content.value?.id;
      if (!isEditMode.value || body?.kind !== "layout" || !widgetId) return;
      const separator = event.currentTarget;
      if (!(separator instanceof HTMLElement)) return;
      const grid = separator.parentElement;
      if (!grid) return;
      event.preventDefault();
      event.stopPropagation();

      const count = axis === "columns" ? body.columns : body.rows;
      const initial =
        (axis === "columns" ? body.columnSizes : body.rowSizes) ??
        Array.from({ length: count }, () => 1);
      const rect = grid.getBoundingClientRect();
      const span =
        (axis === "columns" ? rect.width : rect.height) -
        GRID_GAP * (count - 1);
      if (span <= 0) return;
      // In RTL the first column is on the right: leftwards grows it.
      const direction =
        axis === "columns" && getComputedStyle(grid).direction === "rtl"
          ? -1
          : 1;
      const start = axis === "columns" ? event.clientX : event.clientY;
      const pointerId = event.pointerId;

      // Window-level listeners rather than pointer capture: the pointer
      // leaves the 8px separator immediately, and capture retargeting is
      // unreliable in Electron webviews.
      const onMove = (move: PointerEvent) => {
        if (move.pointerId !== pointerId) return;
        const position = axis === "columns" ? move.clientX : move.clientY;
        // Pixels -> normalized track units (the axis totals `count`).
        const delta = ((position - start) * direction * count) / span;
        trackDrag.value = {
          axis,
          sizes: resizeTrackPair(initial, index, delta),
        };
      };
      const onEnd = (end: PointerEvent) => {
        if (end.pointerId !== pointerId) return;
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onEnd);
        window.removeEventListener("pointercancel", onEnd);
        const sizes = trackDrag.value?.sizes;
        trackDrag.value = null;
        if (!sizes) return;
        // The click that follows the drag's pointerup would land on
        // whatever slot the pointer ended over and change the selection;
        // eat it (it fires synchronously after pointerup, so the timeout
        // removes the guard before any real click).
        const suppressClick = (click: MouseEvent) => {
          click.stopPropagation();
          click.preventDefault();
        };
        window.addEventListener("click", suppressClick, { capture: true });
        setTimeout(() => {
          window.removeEventListener("click", suppressClick, {
            capture: true,
          });
        }, 0);
        props.resizeLayoutTracks(widgetId, axis, sizes);
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onEnd);
      window.addEventListener("pointercancel", onEnd);
    };

    // Only empty slots accept drops; dropping onto a filled slot would
    // destroy its subtree. Remove first, then drop.
    const canAcceptDrop = (event: DragEvent): boolean =>
      isEditMode.value &&
      content.value === null &&
      hasWidgetDropPayload(event.dataTransfer);

    // dragenter/dragleave pair up per hovered descendant; count them so the
    // highlight survives moving across the placeholder text.
    const dragCount = ref(0);
    const isDragOver = computed(() => dragCount.value > 0);

    const onDragEnter = (event: DragEvent) => {
      if (!canAcceptDrop(event)) return;
      dragCount.value += 1;
    };

    const onDragOver = (event: DragEvent) => {
      if (!event.dataTransfer) return;
      event.dataTransfer.dropEffect = canAcceptDrop(event) ? "copy" : "none";
    };

    const onDragLeave = (event: DragEvent) => {
      if (!canAcceptDrop(event)) return;
      dragCount.value = Math.max(0, dragCount.value - 1);
    };

    const onDrop = (event: DragEvent) => {
      dragCount.value = 0;
      if (!isEditMode.value || content.value !== null) return;
      const payload = readWidgetDropPayload(event.dataTransfer);
      if (!payload) return;
      props.dropWidget(props.path, payload);
    };

    const onClick = (event: MouseEvent) => {
      if (!isEditMode.value) return;
      // Stop only in edit mode so regular interactions (clicking through to
      // view rows, document-level dismiss handlers) keep bubbling otherwise.
      event.stopPropagation();
      props.selectSlot(props.path);
    };

    return {
      isSelected,
      isDragOver,
      viewQuery,
      viewDisplay,
      layoutGridStyle,
      layoutChildStyle,
      startTrackResize,
      onDragEnter,
      onDragOver,
      onDragLeave,
      onDrop,
      onClick,
    };
  },
});
</script>

<style scoped lang="scss">
.slot {
  min-width: 0;
  min-height: 48px;
  border-radius: 12px;
  box-sizing: border-box;
  display: flex;
  align-items: stretch;
  justify-content: stretch;
  cursor: default;
  position: relative;

  border: 1px solid transparent;
  background: transparent;
  @include transition(border 0.2s ease-out);

  @include themed() {
    &.editing.empty {
      border-style: dashed;
      border-color: t($grey-30);
      background: t($grey-03);
    }

    &.editing.empty.dragOver {
      border: 1px dashed t($secondary-50);
    }

    // Hovering a slot in edit mode previews the selection — only on the
    // innermost hovered slot (`:not(:has(.slot:hover))`, since :hover also
    // matches every ancestor slot), which is what a click would select.
    // Subtler than the selected border, which it never overrides; nor does
    // it override the drop highlight while dragging.
    &.editing:not(.selected):not(.dragOver):hover:not(:has(.slot:hover)) {
      border-color: t($grey-40);
    }

    // Also matches empty slots (same class count as `.editing.empty` above,
    // and later in the cascade, so the primary border wins there too).
    &.editing.selected {
      border-color: t($primary);
    }
  }
}

.slot.empty {
  padding: 16px;
  align-items: center;
  justify-content: center;
}

// Edit mode: content widgets are inert previews — every pointer event falls
// through to the slot itself, whose click selects the widget for
// configuration in the inspector. Layout grids stay interactive (their
// child slots are drop targets and selectable). The selected view's
// configuration lives in the inspector (teleported DOM, unaffected by
// this rule).
.slot.editing.filled > :not(.layout-grid) {
  pointer-events: none;
  user-select: none;
}

.slot.editing.empty {
  min-height: 96px;
}

.layout-grid {
  display: grid;
  width: 100%;
  min-height: 0;
}

// The drag handle living in each separator track: full-track hit area,
// with a thin bar that lights up on hover to invite the resize.
.track-separator {
  position: relative;
  z-index: 4;
  touch-action: none;

  &.columns {
    cursor: col-resize;
  }

  &.rows {
    cursor: row-resize;
  }

  &::after {
    content: "";
    position: absolute;
    border-radius: 2px;
    background: transparent;
    @include transition(background-color 0.15s ease);
  }

  &.columns::after {
    inset-block: 0;
    inset-inline: 2px;
  }

  &.rows::after {
    inset-inline: 0;
    inset-block: 2px;
  }

  @include themed() {
    &:hover::after {
      background: t($grey-30);
    }
  }
}

// Layout widgets get breathing room so their own frame (selection border)
// does not overlap the children's.
.slot.filled:has(> .layout-grid).editing {
  padding: 12px;

  @include themed() {
    border-color: t($grey-15);
  }

  &.selected {
    @include themed() {
      border-color: t($primary);
    }
  }
}

// Page-mode timeline: grows with the day but stays clamped to the viewport
// (screen mode overrides this below — the slot's height is the clamp).
.slot :deep(.events) {
  width: 100%;
  min-height: 300px;
  max-height: calc(100vh - 280px);
}

// Screen height mode: slots divide a fixed height instead of growing with
// content. The scroll lives on the widget's root element, not on the slot
// itself.
.slot.height-screen {
  min-height: 0;

  &.filled > * {
    width: 100%;
    min-height: 0;
    max-height: 100%;
    overflow-y: auto;
  }

  // Layout widgets never scroll: the grid fills the slot and its child
  // slots handle their own overflow.
  &.filled > .layout-grid {
    height: 100%;
    overflow: visible;
  }
}

.slot :deep(.query) {
  max-width: 100%;
  width: 100%;
}

.slot :deep(.query.wide) {
  max-width: 100%;
  width: 100%;
}

.slot :deep(.query-wrapper) {
  width: 100%;
}

.slot-placeholder {
  @extend %font-caption-regular;
  @include themed() {
    color: t($grey-60);
  }
}
</style>
